/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import renderHTML from 'html-react-parser';
import { animateScroll as scroll } from 'react-scroll';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { XCircle, MagnifyingGlass } from 'phosphor-react';

import {
  fetchCalendarYears,
  fetchCalendarRaces,
  fetchRacesSearch,
} from '../../../services/apis/business/race';

import InputField from '../../../components/fields/input';
import DropdownField from '../../../components/fields/dropdown';

import Spinner from '../../../components/loader/spinner';

import Pagination from '../../../components/layout/pagination';

function RaceCalendar() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [metadata, setMetadata] = useState(null);
  const [years, setYears] = useState([]);
  const [races, setRaces] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(true);

  const { control, setValue, watch } = useForm({
    defaultValues: {
      search: '',
      year: null,
    },
  });

  const search = watch('search');
  const year = watch('year');

  const yearsList = useCallback(async () => {
    const response = await fetchCalendarYears();

    const list = response.map(item => ({
      id: item,
      name: item,
    }));

    setYears(list);
    setLoading(false);
  }, []);

  const racesList = useCallback(async (selectedYear, currentPage) => {
    setFetching(true);

    try {
      const response = await fetchCalendarRaces(selectedYear, currentPage);

      const { races: list, totalDocs, totalPages, hasPrevPage, hasNextPage } = response;

      setRaces(list);
      setMetadata({
        totalDocs,
        totalPages,
        hasPrevPage,
        hasNextPage,
      });
      setSearched(false);
      setFetching(false);
    } catch (error) {
      setFetching(false);
    }
  }, []);

  const racesSearch = useCallback(async (selectedYear, searchTerm) => {
    try {
      const response = await fetchRacesSearch(selectedYear, searchTerm);

      const { races: list } = response;

      setRaces(list);
      setSearched(true);
      setFetching(false);
    } catch (error) {
      setFetching(false);
    }
  }, []);

  const handleSearchKeyDown = useCallback(
    code => {
      if (code === 13) {
        if (search) {
          racesSearch(year.name, search);
        } else {
          racesList(year.name, 1);
        }
      }
    },
    [search, year],
  );

  const handleSearchClick = useCallback(() => {
    if (search) {
      racesSearch(year.name, search);
    } else {
      racesList(year.name, 1);
    }
  }, [search, year]);

  const handleSearchCancel = useCallback(() => {
    setValue('search', '');
    racesList(year.name, 1);
  }, [year]);

  useEffect(() => {
    yearsList().catch(error => {
      setLoading(false);

      throw error;
    });
  }, [yearsList]);

  useEffect(() => {
    if (year) {
      setValue('search', '');

      if (page > 1) {
        setPage(1);
      } else {
        racesList(year.name, page);
      }
    }
  }, [year]);

  useEffect(() => {
    if (years.length) {
      setValue('year', years[0]);
    }
  }, [years]);

  useEffect(() => {
    if (year && page) {
      racesList(year.name, page);
    }
  }, [page]);

  useEffect(() => {
    scroll.scrollToTop({
      duration: 0,
      delay: 0,
      smooth: false,
    });
  }, [races]);

  const renderRegisterButton = race => {
    if (race.isOpen) {
      return (
        <button type="button" className="button mt-4 mb-1">
          Register
        </button>
      );
    }

    if (race.isPast) {
      return (
        <p className="text-sm text-red-600 font-semibold mt-2">
          Registrations have closed on: {race.closeDate}
        </p>
      );
    }

    return (
      <p className="text-sm text-red-600 font-semibold mt-2">
        Registrations will open on: {race.openDate}
      </p>
    );
  };

  return (
    <>
      <Helmet>
        <title>{year ? `Races | ${year?.name}` : 'Loading...'}</title>
      </Helmet>
      <div className="min-height">
        <div className="banner">
          <p>Races</p>
        </div>
        <div className="container max-w-4xl mx-auto py-4 px-6 md:px-8">
          <div className="flex flex-col-reverse sm:flex-row justify-evenly sm:justify-between">
            <div className="flex items-center justify-center sm:justify-start">
              {searched && (
                <button
                  type="button"
                  className="button button--text-only !px-0"
                  style={{ marginRight: '10px' }}
                  onClick={() => handleSearchCancel()}>
                  <XCircle size={20} weight="bold" />
                </button>
              )}
              <InputField
                name="search"
                control={control}
                placeholder="Search By Name"
                onKeyDown={event => handleSearchKeyDown(event.keyCode)}
              />
              <button type="button" className="button ml-2" onClick={() => handleSearchClick()}>
                <MagnifyingGlass size={18} weight="bold" />
              </button>
            </div>
            {false && (
              <DropdownField name="year" control={control} options={years} loading={loading} />
            )}
          </div>
          {fetching && (
            <div className="mt-8">
              <Spinner size="medium" />
            </div>
          )}
          {!fetching &&
            (races.length > 0 ? (
              <>
                <div className="grid grid-cols-12 gap-x-6 gap-y-4 mt-8 mb-6">
                  {races.map((race, index) => (
                    <div
                      key={index}
                      className="col-span-12 sm:col-span-4 xl:col-span-4 flex flex-col duration-1000 transition-all cursor-pointer rounded-xl"
                      onClick={() => navigate(`/race/${race.url}`)}
                      role="presentation">
                      <div className="bg-white flex flex-col h-full shadow-lg rounded-xl relative border border-gray-300 mb-4">
                        <div className="h-full flex items-center justify-center">
                          <LazyLoadImage
                            wrapperClassName="race-logo-wrapper"
                            className="race-logo-image"
                            alt={`${race.name}'s Logo`}
                            effect="blur"
                            src={race.logo}
                          />
                        </div>
                        <div className="text-center pb-4 px-4">
                          <h3 className="text-xl font-bold leading-normal">{race.name}</h3>
                          <p className="text-sm text-gray-500 font-semibold leading-normal mt-2">
                            {renderHTML(race.tag)}
                          </p>
                          <p className="text-sm text-gray-500 font-semibold leading-normal mt-2">
                            Location: {renderHTML(race.location)}
                          </p>
                          <p className="text-sm text-gray-500 font-semibold leading-normal mt-2">
                            Event Date: {race.eventDate}
                          </p>
                          {renderRegisterButton(race)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!searched && (
                  <Pagination
                    currentPage={Number(page)}
                    step={2}
                    metadata={metadata}
                    onChange={value => setPage(value)}
                  />
                )}
              </>
            ) : (
              <div className="py-8">
                <p className="text-gray-700 text-center">No Races Found</p>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

export default RaceCalendar;
